import { useMemo } from 'react';

import { useIsRetailReg } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/hooks/useIsRetailReg';
import { useIsRetailUpgrade } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/hooks/useIsRetailUpgrade';
import { useIsSlotReg } from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/hooks/useIsSlotReg';
import { useIsSlotUpgrade } from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/hooks/useIsSlotUpgrade';
import { useIsJoinStep } from '@interlayer/js/app/components/main/Registration/hooks/useIsJoinStep';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';

type TUseDistributorForRegType<R, U, J, RE, RU, SR, SU> = {
    registration: R;
    upgrade: U;
    joinNow?: J;
    retailReg?: RE;
    retailUpgrade?: RU;
    slotReg?: SR | RE;
    slotUpgrade?: SU | RU;
}

export const useDistributorForRegType = <R, U, J = R, RE = R, RU = R, SR = RE, SU = RU>({
    registration,
    upgrade,
    joinNow,
    retailReg,
    retailUpgrade,
    slotReg = retailReg,
    slotUpgrade = retailUpgrade
}: TUseDistributorForRegType<R, U, J, RE, RU, SR, SU>): R | U | J | RE | RU | SR | SU => {
    const isJoinStep = useIsJoinStep();
    const isRetailReg = useIsRetailReg();
    const isRetailUpgrade = useIsRetailUpgrade();
    const isSlotReg = useIsSlotReg();
    const isSlotUpgrade = useIsSlotUpgrade();
    const { isUpgrade } = useRegistrationSelector();

    return useMemo(() => {
        if (isRetailReg && typeof retailReg !== 'undefined') {
            return retailReg;
        }

        if (isRetailUpgrade && typeof retailUpgrade !== 'undefined') {
            return retailUpgrade;
        }

        if (isSlotReg && typeof slotReg !== 'undefined') {
            return slotReg;
        }

        if (isSlotUpgrade && typeof slotUpgrade !== 'undefined') {
            return slotUpgrade;
        }

        if (isJoinStep && joinNow) {
            return joinNow;
        }

        if (isUpgrade) {
            return upgrade;
        }

        return registration;
    }, [
        isRetailReg, retailReg, isRetailUpgrade, retailUpgrade, isSlotReg, slotReg, isSlotUpgrade, slotUpgrade,
        isJoinStep, joinNow, isUpgrade, registration, upgrade
    ]);
};
